import { Star, StarHalf, Quote, BadgeCheck, PenLine } from "lucide-react";
import { Reveal } from "@/components/MaskedLine";
import { GOOGLE_REVIEWS_URL, GOOGLE_WRITE_REVIEW_URL, GOOGLE_RATING, GOOGLE_REVIEWS_COUNT } from "@/constants/site";

const reviews = [
  {
    author: "Client vérifié Google",
    context: "Entretien d'espace vert",
    date: "Avis Google",
    text: "Très bon travail rapide. Personnel qui connait son boulot. Rapport qualité prix 👌 Entreprise recommandée, à rappeler sans hésiter.",
    highlight: "Rapport qualité-prix remarquable",
  },
  {
    author: "Client particulier",
    context: "Taille d'un bouleau",
    date: "Avis Google",
    text: "Nous avons sollicité leurs services pour la taille d'un bouleau et nous ne sommes pas déçus : un travail rapide, efficace et professionnel avec explications détaillées des actions menées et l'amabilité en plus ! Je recommande chaudement.",
    highlight: "Travail rapide, efficace et professionnel",
  },
];

const Stars = ({ half = false }) => (
  <div className="flex gap-1">
    {[...Array(half ? 4 : 5)].map((_, i) => (
      <Star key={i} className="w-4 h-4 fill-[#D4AF37] text-[#D4AF37]" />
    ))}
    {half && <StarHalf className="w-4 h-4 fill-[#D4AF37] text-[#D4AF37]" />}
  </div>
);

const GoogleG = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" aria-hidden="true">
    <path fill="#4285F4" d="M23.5 12.27c0-.85-.08-1.66-.22-2.45H12v4.64h6.45a5.52 5.52 0 0 1-2.39 3.62v3h3.87c2.26-2.09 3.57-5.16 3.57-8.81z" />
    <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.87-3c-1.08.72-2.45 1.15-4.06 1.15-3.12 0-5.76-2.11-6.7-4.95H1.29v3.1A12 12 0 0 0 12 24z" />
    <path fill="#FBBC05" d="M5.3 14.29A7.2 7.2 0 0 1 4.92 12c0-.8.14-1.57.38-2.29v-3.1H1.29a12 12 0 0 0 0 10.82l4.01-3.14z" />
    <path fill="#EA4335" d="M12 4.77c1.76 0 3.34.61 4.58 1.8l3.44-3.44A11.98 11.98 0 0 0 12 0 12 12 0 0 0 1.29 6.61l4.01 3.1C6.24 6.88 8.88 4.77 12 4.77z" />
  </svg>
);

export const GoogleReviewsSection = () => {
  return (
    <section id="avis" data-testid="google-reviews-section" className="relative py-20 sm:py-28 lg:py-36 bg-[#F5F3EE]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-32">
            <Reveal>
              <p className="font-mono-rb text-xs uppercase tracking-[0.25em] text-[#9E5A3C] mb-6">
                Ils nous font confiance
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-serif-rb text-3xl sm:text-4xl lg:text-5xl text-[#17231A] leading-tight" data-testid="reviews-title">
                Des avis Google <span className="italic text-[#2D5A3F]">élogieux</span>, mérités sur le terrain.
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <div
                data-testid="google-rating-badge"
                className="mt-8 inline-flex items-center gap-4 bg-white rounded-2xl px-6 py-5 shadow-[0_20px_50px_-20px_rgba(18,31,23,0.25)] border border-[#2D5A3F]/10"
              >
                <GoogleG />
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-serif-rb text-3xl font-bold text-[#17231A]">{GOOGLE_RATING}</span>
                    <Stars half />
                  </div>
                  <p className="text-xs text-[#47594D] mt-1">{GOOGLE_REVIEWS_COUNT} avis Google — Montreuil (93)</p>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.3}>
              <a
                href={GOOGLE_REVIEWS_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="reviews-see-all-btn"
                className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[#2D5A3F] border-b-2 border-[#2D5A3F]/30 pb-1 hover:border-[#2D5A3F] transition-colors duration-300"
              >
                Voir tous nos avis sur Google →
              </a>
            </Reveal>
          </div>

          <div className="lg:col-span-7 space-y-6">
            {reviews.map((r, i) => (
              <Reveal key={i} delay={0.15 * i}>
                <article
                  data-testid={`review-card-${i}`}
                  className="group relative bg-white rounded-2xl p-8 sm:p-10 border border-[#2D5A3F]/10 shadow-[0_10px_40px_-20px_rgba(18,31,23,0.15)] transition-transform duration-500 hover:-translate-y-1"
                >
                  <Quote className="absolute top-8 right-8 w-8 h-8 text-[#8CA893]/25" />
                  <div className="flex items-center gap-2 mb-5">
                    <Stars />
                    <span className="flex items-center gap-1 text-xs text-[#47594D] ml-2">
                      <BadgeCheck className="w-3.5 h-3.5 text-[#2D5A3F]" /> {r.date}
                    </span>
                  </div>
                  <p className="font-serif-rb text-lg sm:text-xl text-[#17231A] leading-relaxed italic">
                    « {r.text} »
                  </p>
                  <div className="mt-6 pt-6 border-t border-[#2D5A3F]/10 flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="font-semibold text-sm text-[#17231A]">{r.author}</p>
                      <p className="text-xs text-[#47594D]">{r.context}</p>
                    </div>
                    <span className="text-xs font-mono-rb uppercase tracking-widest text-[#9E5A3C] bg-[#9E5A3C]/10 rounded-full px-3 py-1.5">
                      {r.highlight}
                    </span>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal delay={0.15}>
          <div
            data-testid="leave-review-cta"
            className="mt-14 bg-[#121F17] rounded-3xl px-8 py-10 sm:px-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 relative overflow-hidden rb-grain"
          >
            <div className="relative z-10">
              <h3 className="font-serif-rb text-2xl sm:text-3xl text-[#F5F3EE]">
                Satisfait de notre <span className="italic text-[#8CA893]">intervention</span> ?
              </h3>
              <p className="mt-3 text-[#F5F3EE]/65 text-sm sm:text-base max-w-lg leading-relaxed">
                Votre avis Google nous aide à faire grandir notre entreprise et rassure les futurs
                clients. Il ne prend qu'une minute.
              </p>
            </div>
            <a
              href={GOOGLE_WRITE_REVIEW_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="leave-review-btn"
              className="relative z-10 shrink-0 inline-flex items-center gap-2.5 bg-[#D4AF37] text-[#121F17] font-semibold rounded-full px-7 py-4 hover:bg-[#F5F3EE] transition-colors duration-300"
            >
              <PenLine className="w-4 h-4" />
              Laisser un avis sur Google
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
